import { useCallback, useEffect, useRef, useState } from "react";
import { SummaryCard } from "@/components/results/SummaryCard";
import { ReviewTable } from "@/components/results/ReviewTable";
import { RecommendationsList } from "@/components/results/RecommendationsList";
import { ReviewChatPanel } from "@/components/chat";
import { ReviewConfigurationCard } from "@/components/review/ReviewConfigurationCard";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { cn } from "@/lib/utils";
import { GripVertical, MessageCircle } from "lucide-react";
import type { ReviewRecord } from "@/types";

import {
  CHAT_PANEL_WIDTH_DEFAULT,
  clampChatPanelWidth,
} from "@/lib/chat-panel-width";

const CHAT_COLLAPSED_WIDTH = 44;
const FLOAT_CHAT_WIDTH = 280;
const FLOAT_CHAT_HEIGHT = 360;
const FLOAT_CHAT_MARGIN = 8;

interface ViewReviewTabProps {
  review: ReviewRecord;
  mobileChatOpen?: boolean;
  onMobileChatOpenChange?: (open: boolean) => void;
  hideMobileChatFab?: boolean;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getDefaultFloatPos(): { left: number; top: number } {
  if (typeof window === "undefined") {
    return { left: FLOAT_CHAT_MARGIN, top: FLOAT_CHAT_MARGIN };
  }
  return {
    left: Math.max(FLOAT_CHAT_MARGIN, window.innerWidth - FLOAT_CHAT_WIDTH - 16),
    top: Math.max(FLOAT_CHAT_MARGIN, window.innerHeight - FLOAT_CHAT_HEIGHT - 16),
  };
}

function clampFloatPos(left: number, top: number): { left: number; top: number } {
  if (typeof window === "undefined") return { left, top };
  return {
    left: clamp(left, FLOAT_CHAT_MARGIN, Math.max(FLOAT_CHAT_MARGIN, window.innerWidth - FLOAT_CHAT_WIDTH - FLOAT_CHAT_MARGIN)),
    top: clamp(top, FLOAT_CHAT_MARGIN, Math.max(FLOAT_CHAT_MARGIN, window.innerHeight - FLOAT_CHAT_HEIGHT - FLOAT_CHAT_MARGIN)),
  };
}

export function ViewReviewTab({
  review,
  mobileChatOpen: mobileChatOpenProp,
  onMobileChatOpenChange,
  hideMobileChatFab = false,
}: ViewReviewTabProps) {
  const [chatWidth, setChatWidth] = useLocalStorage<number>(
    "review-chat-width",
    CHAT_PANEL_WIDTH_DEFAULT
  );
  const [isChatCollapsed, setIsChatCollapsed] = useLocalStorage<boolean>("review-chat-collapsed", false);
  const [internalMobileChatOpen, setInternalMobileChatOpen] = useState(false);
  const isChatControlled = onMobileChatOpenChange !== undefined;
  const mobileChatOpen = isChatControlled ? (mobileChatOpenProp ?? false) : internalMobileChatOpen;
  const setMobileChatOpen = isChatControlled ? onMobileChatOpenChange : setInternalMobileChatOpen;
  const [mobileChatFullscreen, setMobileChatFullscreen] = useState(false);
  const [floatPos, setFloatPos] = useState<{ left: number; top: number } | null>(null);
  const resizeDragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const floatDragRef = useRef<{
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
  } | null>(null);

  useEffect(() => {
    const clamped = clampChatPanelWidth(chatWidth);
    if (clamped !== chatWidth) setChatWidth(clamped);
  }, [chatWidth, setChatWidth]);

  const handleToggleChatCollapse = useCallback(() => {
    setIsChatCollapsed(prev => !prev);
  }, [setIsChatCollapsed]);

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      resizeDragRef.current = { startX: event.clientX, startWidth: chatWidth };
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [chatWidth]
  );

  const handleResizePointerMove = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!resizeDragRef.current) return;
      const { startX, startWidth } = resizeDragRef.current;
      const next = clampChatPanelWidth(startWidth + (startX - event.clientX));
      setChatWidth(next);
    },
    [setChatWidth],
  );

  const handleResizePointerUp = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (resizeDragRef.current) {
      resizeDragRef.current = null;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // ignore if already released
      }
    }
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  const openMobileChat = useCallback(() => {
    setFloatPos((prev) => prev ?? getDefaultFloatPos());
    setMobileChatFullscreen(false);
    setMobileChatOpen(true);
  }, []);

  const closeMobileChat = useCallback(() => {
    setMobileChatOpen(false);
    setMobileChatFullscreen(false);
  }, []);

  const handleFloatPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (mobileChatFullscreen) return;
      if ((event.target as HTMLElement).closest("button")) return;
      const origin = floatPos ?? getDefaultFloatPos();
      floatDragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        originLeft: origin.left,
        originTop: origin.top,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [floatPos, mobileChatFullscreen],
  );

  const handleFloatPointerMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!floatDragRef.current) return;
    const { startX, startY, originLeft, originTop } = floatDragRef.current;
    setFloatPos(
      clampFloatPos(originLeft + (event.clientX - startX), originTop + (event.clientY - startY)),
    );
  }, []);

  const handleFloatPointerUp = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!floatDragRef.current) return;
    floatDragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore if already released
    }
  }, []);

  useEffect(() => {
    if (!mobileChatOpen || mobileChatFullscreen) return;
    const onResize = () => {
      setFloatPos((prev) => clampFloatPos(prev?.left ?? getDefaultFloatPos().left, prev?.top ?? getDefaultFloatPos().top));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mobileChatOpen, mobileChatFullscreen]);

  useEffect(() => {
    if (!mobileChatOpen) return;
    setFloatPos((prev) => prev ?? getDefaultFloatPos());
    setMobileChatFullscreen(false);
  }, [mobileChatOpen]);

  return (
    <div className="h-full min-h-0 overflow-hidden">
      <div className="flex h-full min-h-0 gap-0 p-3 md:p-8">
        {/* Left side - Scrollable content */}
        <div className="min-h-0 min-w-0 flex-1 space-y-6 overflow-auto pr-0 md:pr-4">
          <ReviewConfigurationCard
            reviewId={review.id}
            contractType={review.contract_type}
            jurisdiction={review.jurisdiction}
            userRole={review.user_role}
            skipResearch={review.skip_research}
            backgroundInfo={review.background_info}
            agentInstructions={review.agent_instructions}
          />

          <SummaryCard summary={review.summary || ""} overallRiskScore={review.overall_risk_score || 0} />

          <div>
            <h3 className="mb-2.5 text-[13px] font-semibold text-[var(--lm-muted)] dark:mb-3 dark:text-slate-300/85">
              Contract Review Report
            </h3>
            <ReviewTable rows={review.review_table || []} />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-300/85">
              Recommendations
            </h3>
            <RecommendationsList recommendations={review.recommendations || []} />
          </div>
        </div>

        {/* Right side - Resizable chat (xl+) */}
        <div className="hidden min-h-0 shrink-0 xl:flex">
          {!isChatCollapsed ? (
            <button
              type="button"
              aria-label="Resize chat panel"
              className={cn(
                "group relative flex w-3 shrink-0 cursor-col-resize touch-none items-center justify-center rounded-md",
                "border-0 bg-transparent p-0 outline-none",
                "hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-violet-500/50"
              )}
              onPointerDown={handleResizePointerDown}
              onPointerMove={handleResizePointerMove}
              onPointerUp={handleResizePointerUp}
              onPointerCancel={handleResizePointerUp}
            >
              <span className="pointer-events-none flex h-16 w-1.5 items-center justify-center rounded-full bg-white/10 transition-colors group-hover:bg-white/25 group-active:bg-white/35">
                <GripVertical className="h-4 w-4 text-slate-400 opacity-70 group-hover:opacity-100" aria-hidden />
              </span>
            </button>
          ) : null}
          <div
            className="h-full min-h-0 min-w-0"
            style={{
              width: isChatCollapsed ? CHAT_COLLAPSED_WIDTH : clampChatPanelWidth(chatWidth),
            }}
          >
            <ReviewChatPanel
              review={review}
              className="h-full"
              isCollapsed={isChatCollapsed}
              onToggleCollapse={handleToggleChatCollapse}
            />
          </div>
        </div>
      </div>

      {!hideMobileChatFab && !mobileChatOpen ? (
        <button
          type="button"
          className="fixed bottom-4 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 shadow-lg shadow-blue-500/25 xl:hidden"
          aria-label="Open review chat"
          onClick={openMobileChat}
        >
          <MessageCircle className="h-6 w-6 text-white" />
        </button>
      ) : mobileChatOpen ? (
        <div
          className={cn(
            "fixed z-40 flex flex-col overflow-hidden border border-white/10 bg-slate-900 shadow-2xl xl:hidden",
            mobileChatFullscreen ? "inset-0 h-auto w-auto rounded-none" : "rounded-2xl",
          )}
          style={
            mobileChatFullscreen
              ? undefined
              : {
                  left: (floatPos ?? getDefaultFloatPos()).left,
                  top: (floatPos ?? getDefaultFloatPos()).top,
                  width: FLOAT_CHAT_WIDTH,
                  height: FLOAT_CHAT_HEIGHT,
                }
          }
        >
          <ReviewChatPanel
            review={review}
            className="h-full rounded-none border-0"
            isFullscreen={mobileChatFullscreen}
            onToggleFullscreen={() => setMobileChatFullscreen((v) => !v)}
            onClose={closeMobileChat}
            headerDragHandlers={
              mobileChatFullscreen
                ? undefined
                : {
                    onPointerDown: handleFloatPointerDown,
                    onPointerMove: handleFloatPointerMove,
                    onPointerUp: handleFloatPointerUp,
                    onPointerCancel: handleFloatPointerUp,
                  }
            }
          />
        </div>
      ) : null}
    </div>
  );
}
